import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import Reveal from "@/components/Reveal";

// Modern curved-glass facade with balcony rhythm (rishi) — anonymous, signage-free, bright.
// Animated via CSS (horizontal pan) — no video, no extra assets.
const HERO_POSTER =
  "https://images.unsplash.com/photo-1763811939388-39b3c3fa23b5?auto=format&fit=crop&w=2400&q=80";

// "Backed by ZT" portrait — locally cleaned (Gemini Nano Banana inpainted out a small bank logo
// from the original Unsplash skyscraper photo).
const ABSTRACT_LINES = "/images/backed-by-zt.jpg";

const SERVICES = [
  { title: "Capital Structuring", body: "Institutional debt, institutional equity and hybrid capital solutions." },
  { title: "Strategic Advisory", body: "Cross border transactions, partnerships and market expansion." },
  { title: "Principal Investments", body: "Co investments alongside select sponsors and operators." },
  { title: "Platform Expansion", body: "Strategic growth and GCC market entry." },
];

const PLATFORMS = [
  { v: "Automotive", b: "US dealership platforms and consolidation strategies" },
  { v: "Healthcare", b: "Operator led freestanding ER and outpatient platforms" },
  { v: "Real Estate", b: "Land development and operator backed real estate platforms" },
  { v: "Industrial & Equipment Platforms", b: "Industrial and equipment platforms with asset backed financing strategies" },
  { v: "Sports & Consumer Infrastructure", b: "Sports infrastructure and consumer platforms" },
];

const STATS = [
  { value: "$800B+", label: "Combined Transaction Experience" },
  { value: "75+ Years", label: "Senior Leadership Across GCC, US & Europe" },
  { value: "29 Years", label: "ZT Heritage as Owner Operator" },
];

/* ============================== HERO ============================== */
const HeroSection = () => (
  <section
    className="relative min-h-[100svh] md:h-screen md:min-h-[640px] w-full overflow-hidden bg-ivory text-navy"
    data-testid="hero-section"
  >
    <div className="hero-pan-wrap" aria-hidden="true">
      <img src={HERO_POSTER} alt="" />
    </div>

    {/* Base wash — keeps the image visible while taking the edge off mobile contrast. */}
    <div className="absolute inset-0 bg-ivory/35 md:hidden" />
    <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-ivory/55 via-ivory/15 to-transparent" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(200,169,97,0.06),transparent_55%)]" />

    {/* Mobile wash strip — anchored to the bottom edge of the hero, feathered at top. */}
    <div
      aria-hidden="true"
      className="md:hidden absolute inset-x-0 bottom-0 z-[5] pointer-events-none"
      style={{
        height: "70%",
        background:
          "linear-gradient(to top, rgba(253,251,247,0.97) 0%, rgba(253,251,247,0.97) 55%, rgba(253,251,247,0.65) 78%, transparent 100%)",
      }}
    />

    {/* Desktop wash strip — flows from the viewport's left edge, feathered top + bottom. */}
    <div
      aria-hidden="true"
      className="hidden md:block absolute left-0 right-0 z-[5] pointer-events-none"
      style={{
        top: "32%",
        bottom: "0%",
        background:
          "linear-gradient(to right, rgba(253,251,247,0.96) 0%, rgba(253,251,247,0.96) 32%, rgba(253,251,247,0.55) 48%, rgba(253,251,247,0.10) 64%, transparent 78%)",
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent 0%, black 18%, black 88%, transparent 100%)",
        maskImage:
          "linear-gradient(to bottom, transparent 0%, black 18%, black 88%, transparent 100%)",
      }}
    />

    <div className="relative z-10 min-h-[100svh] md:min-h-0 md:h-full container-zt flex flex-col justify-end pb-16 pt-32 md:pb-24 md:pt-40">
      <Reveal delay={150}>
        <h1
          className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-[80px] leading-[1.05] tracking-tight text-navy max-w-5xl"
          data-testid="hero-headline"
        >
          Institutional Capital.
          <br />
          <span className="italic font-light">Structured</span>. <span className="italic font-light">Deployed</span>.
        </h1>
      </Reveal>
      <Reveal delay={320}>
        <p className="mt-6 md:mt-8 max-w-2xl text-sm sm:text-base md:text-lg text-navy/85 font-light leading-relaxed">
          ZT Global is an independent, DIFC based merchant banking and strategic advisory platform
          focused on capital structuring, principal investments and cross border
          transactions across the US and GCC.
        </p>
      </Reveal>
      <Reveal delay={480} className="mt-8 md:mt-12 flex flex-wrap items-center gap-4 md:gap-5">
        <Link to="/contact" className="btn-navy" data-testid="hero-cta-primary">
          Begin a Conversation <ArrowRight size={14} />
        </Link>
        <Link to="/leadership" className="btn-outline-dark" data-testid="hero-cta-secondary">
          Meet the Principals
        </Link>
      </Reveal>
    </div>

    <div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-navy/60">
      <span className="text-[10px] tracking-[0.32em] uppercase">Scroll</span>
      <ArrowDown size={14} className="animate-scroll-indicator" />
    </div>
  </section>
);

/* ============================== GEOMETRIC PATTERN ============================== */
const GeometricPattern = () => (
  <div
    className="pointer-events-none absolute -left-24 md:-left-40 w-[520px] md:w-[780px] aspect-square opacity-[0.07] text-gold-600 z-0"
    style={{ top: "22%" }}
    aria-hidden="true"
  >
    <svg viewBox="0 0 200 200" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="0.6">
      <circle cx="100" cy="100" r="98" />
      <circle cx="100" cy="100" r="82" />
      <rect x="32" y="32" width="136" height="136" transform="rotate(0 100 100)" />
      <rect x="32" y="32" width="136" height="136" transform="rotate(45 100 100)" />
      <rect x="55" y="55" width="90" height="90" transform="rotate(0 100 100)" />
      <rect x="55" y="55" width="90" height="90" transform="rotate(45 100 100)" />
      <circle cx="100" cy="100" r="34" />
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * Math.PI) / 8;
        return (
          <line
            key={`spoke-${i}`}
            x1={100 + Math.cos(a) * 34}
            y1={100 + Math.sin(a) * 34}
            x2={100 + Math.cos(a) * 82}
            y2={100 + Math.sin(a) * 82}
          />
        );
      })}
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * Math.PI) / 4;
        const cx = 100 + Math.cos(a) * 58;
        const cy = 100 + Math.sin(a) * 58;
        return <circle key={`petal-${i}`} cx={cx} cy={cy} r="18" />;
      })}
    </svg>
  </div>
);

/* ============================== INTRO ============================== */
const IntroSection = () => (
  <section className="relative py-24 md:py-36" data-testid="intro-section">
    <div className="relative z-10 container-zt grid grid-cols-1 md:grid-cols-12 gap-12">
      <Reveal className="md:col-span-5">
        <p className="label-zt text-gold-600">A New Platform</p>
        <div className="hairline-gold mt-3" />
        <h2 className="mt-8 font-serif text-4xl md:text-5xl text-navy leading-[1.05] tracking-tight">
          Built for the <span className="italic">institutional</span> investor.
        </h2>
      </Reveal>
      <Reveal delay={150} className="md:col-span-7 md:pt-2">
        <p className="text-lg leading-relaxed text-navy/80 font-light">
          ZT Global advises on capital structuring, strategic transactions and
          principal investments across healthcare, automotive, real estate,
          industrial and athletics platforms.
        </p>
        <p className="mt-6 text-lg leading-relaxed text-navy/80 font-light">
          The firm combines senior investment banking experience with an owner
          operator perspective across the US and GCC.
        </p>
      </Reveal>
    </div>
  </section>
);

/* ============================== EDITORIAL BANNER ============================== */
const EditorialBanner = () => (
  <section className="relative py-16 md:py-24" data-testid="diptych-section">
    <div className="container-zt relative z-10">
      <div className="relative w-full aspect-[16/9] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1496564203457-11bb12075d90?auto=format&fit=crop&w=2400&q=80"
          alt="Modernist architectural structure at dusk — institutional permanence"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ filter: "saturate(0.85) contrast(1.05)" }}
        />
        {/* Deep navy duotone wash so the image always reads as ZT brand */}
        <div className="absolute inset-0 bg-navy/55" />
        <div className="absolute inset-0 bg-gradient-to-tr from-navy/85 via-navy/55 to-[rgba(200,169,97,0.18)] mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-transparent" />
        {/* Subtle film grain */}
        <div
          className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 ring-1 ring-gold/15" />
        <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 max-w-md">
          <p className="label-zt text-gold/90">Anchored in DIFC</p>
          <div className="h-px w-10 bg-gold/60 mt-3" />
          <p className="mt-4 font-serif italic text-white/95 text-xl md:text-2xl leading-snug tracking-tight">
            Discreet capital. Long horizons.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============================== SERVICES ============================== */
const ServicesSection = () => (
  <section className="bg-ivory py-24 md:py-36" data-testid="services-section">
    <div className="container-zt">
      <Reveal>
        <p className="label-zt text-gold-600">What We Do</p>
        <div className="hairline-gold mt-3" />
      </Reveal>
      <Reveal delay={120}>
        <h2 className="mt-8 font-serif text-4xl md:text-5xl text-navy max-w-3xl leading-[1.05] tracking-tight">
          Four areas of <span className="italic">disciplined</span> focus.
        </h2>
      </Reveal>

      <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
        {SERVICES.map((s) => (
          <Reveal key={s.title} className="border-t border-gold/50 pt-6" data-testid={`service-${s.title}`}>
            <h3 className="font-serif text-2xl md:text-3xl text-navy tracking-tight">{s.title}</h3>
            <p className="mt-4 text-navy/70 font-light leading-relaxed text-base md:text-lg">{s.body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ============================== PLATFORMS ============================== */
const PlatformsSection = () => (
  <section className="bg-white py-24 md:py-36 border-t border-navy/10" data-testid="platforms-section">
    <div className="container-zt grid grid-cols-1 md:grid-cols-12 gap-12">
      <Reveal className="md:col-span-5">
        <p className="label-zt text-gold-600">Our Platforms</p>
        <div className="hairline-gold mt-3" />
        <h2 className="mt-8 font-serif text-4xl md:text-5xl text-navy leading-[1.05] tracking-tight">
          Owner operated. <span className="italic">Institutional grade</span>.
        </h2>
      </Reveal>
      <Reveal delay={150} className="md:col-span-7 md:pt-2">
        <p className="text-lg leading-relaxed text-navy/80 font-light">
          ZT Global provides structured capital solutions and advisory services to a
          select group of operating platforms with proven track records and
          institutional growth potential across the following verticals:
        </p>
      </Reveal>
    </div>

    <div className="container-zt mt-16">
      <ul className="divide-y divide-navy/10 border-t border-b border-navy/15">
        {PLATFORMS.map((p, i) => (
          <Reveal
            key={p.v}
            as="li"
            delay={i * 80}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 py-7 md:py-8"
            data-testid={`platform-${p.v}`}
          >
            <div className="md:col-span-4 flex items-center gap-5">
              <span className="font-sans label-zt text-gold-600">0{i + 1}</span>
              <h3 className="font-serif text-2xl md:text-3xl text-navy tracking-tight">{p.v}</h3>
            </div>
            <p className="md:col-span-8 text-base md:text-lg text-navy/75 font-light leading-relaxed self-center">
              {p.b}
            </p>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

/* ============================== VISION + STATS ============================== */
const VisionSection = () => (
  <section className="bg-ivory py-24 md:py-36" data-testid="vision-section">
    <div className="container-zt grid grid-cols-1 md:grid-cols-12 gap-12">
      <Reveal className="md:col-span-5">
        <p className="label-zt text-gold-600">The ZT Global Vision</p>
        <div className="hairline-gold mt-3" />
        <h2 className="mt-8 font-serif text-4xl md:text-5xl text-navy leading-[1.05] tracking-tight">
          Lower the cost of capital. <span className="italic text-gold-600">Accelerate</span> growth.
        </h2>
      </Reveal>
      <Reveal delay={150} className="md:col-span-7 md:pt-2">
        <p className="text-lg leading-relaxed text-navy/80 font-light">
          Through disciplined structuring and an institutional approach, ZT Global
          enhances the bankability of opportunities, lowers the cost of capital for
          its platform partners and supports scalable, long term capital deployment
          across sectors and geographies.
        </p>
      </Reveal>
    </div>

    <div className="container-zt mt-20 md:mt-28">
      <div className="grid grid-cols-1 md:grid-cols-3 bg-navy">
        {STATS.map((s, i) => (
          <Reveal
            key={s.label}
            delay={i * 120}
            className={`py-12 md:py-16 ${i !== 0 ? "md:border-l border-white/10" : ""} text-center px-6`}
            data-testid={`stat-${i}`}
          >
            <div className="font-serif text-4xl md:text-5xl text-white tracking-tight">{s.value}</div>
            <div className="mt-4 label-zt text-gold">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ============================== BACKED BY ZT ============================== */
const BackedBySection = () => (
  <section className="bg-ivory pb-24 md:pb-36" data-testid="backed-section">
    <div className="container-zt grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
      <Reveal className="md:col-span-6 order-2 md:order-1">
        <p className="label-zt text-gold-600">Backed by the ZT Platform</p>
        <div className="hairline-gold mt-3" />
        <h2 className="mt-8 font-serif text-4xl md:text-5xl text-navy leading-[1.05] tracking-tight">
          An extension of an <span className="italic text-gold-600">owner-operator</span> mindset.
        </h2>
        <p className="mt-8 text-lg leading-relaxed text-navy/80 font-light">
          ZT Global is built as an extension of ZT's long-term owner-operator
          philosophy. Founded in 1997, ZT has built a diversified portfolio across
          healthcare, automotive, real estate, equipment and youth sports —
          generating approximately US$2 billion in annual portfolio revenue.
        </p>
        <div className="mt-10 grid grid-cols-2 gap-8 max-w-md">
          <div>
            <p className="font-serif text-4xl text-navy">1997</p>
            <p className="mt-2 label-zt text-gold-600">Founded</p>
          </div>
          <div>
            <p className="font-serif text-4xl text-navy">$2B</p>
            <p className="mt-2 label-zt text-gold-600">Annual Portfolio Revenue</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={150} className="md:col-span-6 order-1 md:order-2">
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-navy">
          <img
            src={ABSTRACT_LINES}
            alt="Abstract flowing lines representing capital flow"
            className="absolute inset-0 h-full w-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-navy/30 mix-blend-multiply" />
          <div className="absolute inset-0 ring-1 ring-gold/20" />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ============================== CTA ============================== */
const HomeCTASection = () => (
  <section className="bg-white py-24 md:py-32 border-t border-navy/10" data-testid="home-cta-section">
    <div className="container-zt grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
      <Reveal className="md:col-span-8">
        <p className="label-zt text-gold-600">Begin a Conversation</p>
        <div className="hairline-gold mt-3" />
        <h2 className="mt-8 font-serif text-4xl md:text-6xl text-navy leading-[1.02] tracking-tight">
          Long term partnerships. <br />
          <span className="italic">Thoughtful</span> capital deployment.
        </h2>
        <p className="mt-6 text-lg text-navy/75 font-light max-w-2xl">
          Anchored in DIFC, with a global investment and advisory focus.
        </p>
      </Reveal>
      <Reveal delay={150} className="md:col-span-4 md:pb-2">
        <Link to="/contact" className="btn-navy" data-testid="home-cta-contact">
          Contact ZT Global <ArrowRight size={14} />
        </Link>
      </Reveal>
    </div>
  </section>
);

/* ============================== HOME ============================== */
const Home = () => (
  <>
    <HeroSection />

    {/* Intro + editorial share a single relative wrapper so the geometric motif
        can flow naturally from the intro section down into the editorial section. */}
    <div className="relative bg-ivory" data-testid="intro-wrap">
      <GeometricPattern />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(200,169,97,0.06),transparent_55%)] z-0" />
      <IntroSection />
      <EditorialBanner />
    </div>

    <ServicesSection />
    <PlatformsSection />
    <VisionSection />
    <BackedBySection />
    <HomeCTASection />
  </>
);

export default Home;
